/**
 * SYMPTOM-TREATMENT MAP
 * 
 * Mapeo de síntomas/problemas a tratamientos recomendados.
 * Basado en evidencia científica y buenas prácticas clínicas.
 * 
 * USOS:
 * - Recomendar prácticas al paciente según lo que dice
 * - Sugerir contenido relevante al terapeuta
 * - Auto-categorizar materiales subidos
 */

import { ConceptId } from './ontology';

// ============================================
// TIPOS
// ============================================

export type EvidenceLevel = 'alta' | 'moderada' | 'baja' | 'emergente';
export type EffectTiming = 'inmediato' | 'minutos' | 'dias' | 'semanas';

export interface TreatmentRecommendation {
  techniqueId: ConceptId;
  evidenceLevel: EvidenceLevel;
  effectSize: number;           // 0-1, qué tan efectivo es
  timeToEffect: EffectTiming;
  bestFor: string[];            // Situaciones donde es más útil
  duration?: number;            // Duración típica en minutos
  difficulty: 'easy' | 'medium' | 'hard';
  requiresTherapist?: boolean;  // ¿Necesita guía profesional?
  contraindications?: string[]; // Cuándo NO usar
}

export interface SymptomMapping {
  id: string;
  label: string;
  labelEs: string;
  category: ConceptId;
  
  // Manifestaciones comunes
  manifestations: string[];
  
  // Tratamientos ordenados por evidencia/efectividad
  recommendedTreatments: TreatmentRecommendation[];
  
  // Señales de alerta (requiere profesional)
  redFlags?: string[];
}

// ============================================
// MAPEO SÍNTOMAS → TRATAMIENTOS
// ============================================

export const SYMPTOM_TREATMENT_MAP: Record<string, SymptomMapping> = {
  
  // ==========================================
  // ANSIEDAD
  // ==========================================
  
  "ansiedad": {
    id: "ansiedad",
    label: "Anxiety",
    labelEs: "Ansiedad",
    category: "manejo_ansiedad",
    
    manifestations: [
      "preocupación excesiva",
      "dificultad para relajarse",
      "tensión muscular",
      "problemas para concentrarse",
      "irritabilidad",
      "problemas de sueño",
      "pensamientos catastróficos",
      "evitación de situaciones",
      "sensación de peligro inminente"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "respiracion_4_7_8",
        evidenceLevel: "moderada",
        effectSize: 0.75,
        timeToEffect: "inmediato",
        bestFor: ["crisis aguda", "antes de dormir", "ansiedad anticipatoria"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "respiracion_diafragmatica",
        evidenceLevel: "alta",
        effectSize: 0.80,
        timeToEffect: "inmediato",
        bestFor: ["ansiedad general", "prevención", "uso diario"],
        duration: 5,
        difficulty: "easy"
      },
      {
        techniqueId: "grounding_5_4_3_2_1",
        evidenceLevel: "moderada",
        effectSize: 0.70,
        timeToEffect: "inmediato",
        bestFor: ["disociación", "pánico", "desconexión del presente"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "relajacion_progresiva",
        evidenceLevel: "alta",
        effectSize: 0.82,
        timeToEffect: "minutos",
        bestFor: ["tensión crónica", "antes de dormir", "ansiedad somática"],
        duration: 15,
        difficulty: "medium"
      },
      {
        techniqueId: "body_scan",
        evidenceLevel: "alta",
        effectSize: 0.78,
        timeToEffect: "minutos",
        bestFor: ["tensión corporal", "consciencia somática"],
        duration: 10,
        difficulty: "medium"
      },
      {
        techniqueId: "tecnica_stop",
        evidenceLevel: "moderada",
        effectSize: 0.65,
        timeToEffect: "inmediato",
        bestFor: ["momento de crisis", "espiral de pensamientos"],
        duration: 1,
        difficulty: "easy"
      },
      {
        techniqueId: "exposicion_gradual",
        evidenceLevel: "alta",
        effectSize: 0.90,
        timeToEffect: "semanas",
        bestFor: ["fobias", "evitación", "ansiedad social"],
        difficulty: "hard",
        requiresTherapist: true
      }
    ],
    
    redFlags: [
      "pensamientos suicidas",
      "ataques de pánico frecuentes",
      "incapacidad para funcionar",
      "aislamiento total"
    ]
  },
  
  // ==========================================
  // ESTRÉS
  // ==========================================
  
  "estres": {
    id: "estres",
    label: "Stress",
    labelEs: "Estrés",
    category: "manejo_estres",
    
    manifestations: [
      "agotamiento",
      "irritabilidad",
      "dificultad para desconectar",
      "tensión física",
      "dolores de cabeza",
      "problemas digestivos",
      "olvidos frecuentes",
      "sensación de sobrecarga"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "respiracion_diafragmatica",
        evidenceLevel: "alta",
        effectSize: 0.78,
        timeToEffect: "inmediato",
        bestFor: ["momento de estrés", "pausa en el trabajo"],
        duration: 5,
        difficulty: "easy"
      },
      {
        techniqueId: "coherencia_cardiaca",
        evidenceLevel: "alta",
        effectSize: 0.82,
        timeToEffect: "minutos",
        bestFor: ["estrés crónico", "regulación diaria"],
        duration: 5,
        difficulty: "easy"
      },
      {
        techniqueId: "body_scan",
        evidenceLevel: "alta",
        effectSize: 0.75,
        timeToEffect: "minutos",
        bestFor: ["tensión acumulada", "fin del día"],
        duration: 10,
        difficulty: "medium"
      },
      {
        techniqueId: "meditacion_guiada",
        evidenceLevel: "alta",
        effectSize: 0.80,
        timeToEffect: "minutos",
        bestFor: ["desconexión", "pausa mental"],
        duration: 10,
        difficulty: "easy"
      },
      {
        techniqueId: "relajacion_progresiva",
        evidenceLevel: "alta",
        effectSize: 0.80,
        timeToEffect: "minutos",
        bestFor: ["tensión muscular", "antes de dormir"],
        duration: 15,
        difficulty: "medium"
      }
    ],
    
    redFlags: [
      "burnout severo",
      "síntomas físicos graves",
      "incapacidad para trabajar"
    ]
  },
  
  // ==========================================
  // INSOMNIO / SUEÑO
  // ==========================================
  
  "insomnio": {
    id: "insomnio",
    label: "Insomnia",
    labelEs: "Problemas de Sueño",
    category: "sueno_descanso",
    
    manifestations: [
      "dificultad para conciliar el sueño",
      "despertares nocturnos",
      "despertar muy temprano",
      "sueño no reparador",
      "pensamientos acelerados al acostarse",
      "preocupación por no dormir"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "respiracion_4_7_8",
        evidenceLevel: "moderada",
        effectSize: 0.72,
        timeToEffect: "inmediato",
        bestFor: ["dificultad para conciliar", "mente acelerada"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "body_scan",
        evidenceLevel: "alta",
        effectSize: 0.80,
        timeToEffect: "minutos",
        bestFor: ["tensión nocturna", "relajación previa al sueño"],
        duration: 15,
        difficulty: "easy"
      },
      {
        techniqueId: "relajacion_progresiva",
        evidenceLevel: "alta",
        effectSize: 0.85,
        timeToEffect: "minutos",
        bestFor: ["tensión física", "insomnio crónico"],
        duration: 20,
        difficulty: "medium"
      },
      {
        techniqueId: "meditacion_guiada",
        evidenceLevel: "moderada",
        effectSize: 0.70,
        timeToEffect: "minutos",
        bestFor: ["calmar la mente", "rutina nocturna"],
        duration: 10,
        difficulty: "easy"
      }
    ],
    
    redFlags: [
      "insomnio severo prolongado",
      "apnea del sueño",
      "uso de sustancias para dormir"
    ]
  },
  
  // ==========================================
  // TRISTEZA / DEPRESIÓN
  // ==========================================
  
  "tristeza": {
    id: "tristeza",
    label: "Sadness",
    labelEs: "Tristeza",
    category: "manejo_tristeza",
    
    manifestations: [
      "bajo estado de ánimo",
      "pérdida de interés",
      "fatiga",
      "dificultad para disfrutar",
      "aislamiento",
      "llanto frecuente",
      "sensación de vacío"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "loving_kindness",
        evidenceLevel: "alta",
        effectSize: 0.75,
        timeToEffect: "dias",
        bestFor: ["autocrítica", "sensación de vacío", "soledad"],
        duration: 10,
        difficulty: "medium"
      },
      {
        techniqueId: "meditacion_guiada",
        evidenceLevel: "moderada",
        effectSize: 0.65,
        timeToEffect: "minutos",
        bestFor: ["desconexión", "momento de calma"],
        duration: 10,
        difficulty: "easy"
      },
      {
        techniqueId: "respiracion_diafragmatica",
        evidenceLevel: "moderada",
        effectSize: 0.55,
        timeToEffect: "inmediato",
        bestFor: ["activación básica", "empezar el día"],
        duration: 5,
        difficulty: "easy"
      }
    ],
    
    redFlags: [
      "pensamientos suicidas",
      "autolesiones",
      "incapacidad para funcionar",
      "síntomas severos más de 2 semanas"
    ]
  },
  
  // ==========================================
  // IRA / ENFADO
  // ==========================================
  
  "ira": {
    id: "ira",
    label: "Anger",
    labelEs: "Ira / Enfado",
    category: "manejo_ira",
    
    manifestations: [
      "irritabilidad",
      "explosiones de ira",
      "tensión física",
      "pensamientos hostiles",
      "dificultad para calmarse",
      "arrepentimiento después"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "respiracion_cuadrada",
        evidenceLevel: "moderada",
        effectSize: 0.70,
        timeToEffect: "inmediato",
        bestFor: ["momento de enfado", "antes de responder"],
        duration: 2,
        difficulty: "easy"
      },
      {
        techniqueId: "tecnica_stop",
        evidenceLevel: "moderada",
        effectSize: 0.75,
        timeToEffect: "inmediato",
        bestFor: ["impulso de reaccionar", "frenar escalada"],
        duration: 1,
        difficulty: "easy"
      },
      {
        techniqueId: "grounding_5_4_3_2_1",
        evidenceLevel: "moderada",
        effectSize: 0.65,
        timeToEffect: "inmediato",
        bestFor: ["desconectar del enfado", "volver al presente"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "relajacion_progresiva",
        evidenceLevel: "alta",
        effectSize: 0.78,
        timeToEffect: "minutos",
        bestFor: ["después del enfado", "tensión acumulada"],
        duration: 15,
        difficulty: "medium"
      }
    ],
    
    redFlags: [
      "violencia hacia otros",
      "autolesiones",
      "destrucción de propiedad",
      "amenazas"
    ]
  },
  
  // ==========================================
  // PÁNICO
  // ==========================================
  
  "panico": {
    id: "panico",
    label: "Panic",
    labelEs: "Pánico",
    category: "manejo_ansiedad",
    
    manifestations: [
      "miedo intenso repentino",
      "taquicardia",
      "dificultad para respirar",
      "sudoración",
      "temblores",
      "sensación de muerte inminente",
      "despersonalización"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "grounding_5_4_3_2_1",
        evidenceLevel: "moderada",
        effectSize: 0.80,
        timeToEffect: "inmediato",
        bestFor: ["durante el ataque", "volver a la realidad"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "respiracion_cuadrada",
        evidenceLevel: "moderada",
        effectSize: 0.75,
        timeToEffect: "inmediato",
        bestFor: ["hiperventilación", "calmar el cuerpo"],
        duration: 3,
        difficulty: "easy"
      },
      {
        techniqueId: "enraizamiento",
        evidenceLevel: "moderada",
        effectSize: 0.70,
        timeToEffect: "inmediato",
        bestFor: ["disociación", "sentirse desconectado"],
        duration: 2,
        difficulty: "easy"
      }
    ],
    
    redFlags: [
      "ataques muy frecuentes",
      "evitación severa",
      "agorafobia"
    ]
  },
  
  // ==========================================
  // CONCENTRACIÓN
  // ==========================================
  
  "concentracion": {
    id: "concentracion",
    label: "Concentration",
    labelEs: "Problemas de Concentración",
    category: "atencion_plena",
    
    manifestations: [
      "mente dispersa",
      "dificultad para enfocarse",
      "distracciones constantes",
      "olvidos",
      "procrastinación"
    ],
    
    recommendedTreatments: [
      {
        techniqueId: "meditacion_silencio",
        evidenceLevel: "alta",
        effectSize: 0.80,
        timeToEffect: "semanas",
        bestFor: ["entrenamiento de atención", "práctica regular"],
        duration: 10,
        difficulty: "medium"
      },
      {
        techniqueId: "respiracion_diafragmatica",
        evidenceLevel: "moderada",
        effectSize: 0.60,
        timeToEffect: "inmediato",
        bestFor: ["antes de una tarea", "reset mental"],
        duration: 3,
        difficulty: "easy"
      }
    ]
  }
};

// ============================================
// FUNCIONES DE UTILIDAD
// ============================================

/**
 * Obtiene las recomendaciones de tratamiento para un síntoma
 */
export function getTreatmentsForSymptom(symptomId: string): TreatmentRecommendation[] {
  const mapping = SYMPTOM_TREATMENT_MAP[symptomId];
  return mapping?.recommendedTreatments || [];
}

/**
 * Obtiene los tratamientos más efectivos para un síntoma
 */
export function getTopTreatments(symptomId: string, count: number = 3): TreatmentRecommendation[] {
  const treatments = getTreatmentsForSymptom(symptomId);
  return treatments
    .sort((a, b) => b.effectSize - a.effectSize)
    .slice(0, count);
}

/**
 * Obtiene tratamientos que funcionan para múltiples síntomas
 */
export function getTreatmentsForMultipleSymptoms(
  symptomIds: string[]
): Array<{ techniqueId: ConceptId; symptoms: string[]; avgEffectSize: number }> {
  const treatmentMap = new Map<ConceptId, { symptoms: string[]; totalEffect: number }>();
  
  for (const symptomId of symptomIds) {
    const treatments = getTreatmentsForSymptom(symptomId);
    
    for (const treatment of treatments) {
      const existing = treatmentMap.get(treatment.techniqueId);
      if (existing) {
        existing.symptoms.push(symptomId);
        existing.totalEffect += treatment.effectSize;
      } else {
        treatmentMap.set(treatment.techniqueId, {
          symptoms: [symptomId],
          totalEffect: treatment.effectSize
        });
      }
    }
  }
  
  // Convertir a array y ordenar por número de síntomas que trata
  return Array.from(treatmentMap.entries())
    .map(([techniqueId, data]) => ({
      techniqueId,
      symptoms: data.symptoms,
      avgEffectSize: data.totalEffect / data.symptoms.length
    }))
    .sort((a, b) => {
      // Primero por número de síntomas, luego por efectividad
      if (b.symptoms.length !== a.symptoms.length) {
        return b.symptoms.length - a.symptoms.length;
      }
      return b.avgEffectSize - a.avgEffectSize;
    });
}

/**
 * Verifica si hay red flags en los síntomas mencionados
 */
export function checkRedFlags(symptomIds: string[]): string[] {
  const allRedFlags: string[] = [];
  
  for (const symptomId of symptomIds) {
    const mapping = SYMPTOM_TREATMENT_MAP[symptomId];
    if (mapping?.redFlags) {
      allRedFlags.push(...mapping.redFlags);
    }
  }
  
  return Array.from(new Set(allRedFlags));
}

/**
 * Obtiene tratamientos fáciles para emergencias
 */
export function getEmergencyTreatments(symptomId: string): TreatmentRecommendation[] {
  const treatments = getTreatmentsForSymptom(symptomId);
  return treatments.filter(t => 
    t.difficulty === 'easy' && 
    t.timeToEffect === 'inmediato' &&
    t.duration && t.duration <= 5
  );
}

/**
 * Busca síntoma por término (normalizado)
 */
export function findSymptomByTerm(term: string): SymptomMapping | undefined {
  const normalized = term.toLowerCase().trim();
  
  // Buscar por ID
  if (SYMPTOM_TREATMENT_MAP[normalized]) {
    return SYMPTOM_TREATMENT_MAP[normalized];
  }
  
  // Buscar en manifestaciones
  for (const mapping of Object.values(SYMPTOM_TREATMENT_MAP)) {
    if (mapping.manifestations.some(m => m.toLowerCase().includes(normalized))) {
      return mapping;
    }
    if (mapping.labelEs.toLowerCase().includes(normalized)) {
      return mapping;
    }
  }
  
  return undefined;
}

